import { IncomeStreamType } from "src/types/incomeStreams";
import {
  AnnualSubmissionDataTypes,
  nonFinancialoptionsType,
} from "src/types/itsa";
import { Report } from "../types/tax";

export const PAYMENT_DIRECTION_TYPES = {
  PAYMENT_IN: "PAYMENT_IN",
  PAYMENT_OUT: "PAYMENT_OUT",
};

export const ACCOUNT_NATURE_TYPES = {
  CURRENT: "CURRENT",
  CREDIT_CARD: "CREDIT_CARD",
  SAVINGS: "SAVINGS",
  VAULT: "VAULT",
  OTHER: "OTHER",
};

export const defaultGlobalErrorMessage =
  "We’re really sorry. Please try again or contact our support team at support@getcoconut.com.";

export enum LegalSetup {
  soleTrader = "SOLE_TRADER",
  limitedCompany = "LTD",
}

export const CURRENCY_CODES = {
  GBP: "GBP",
};

export const INCOME_STREAM_TYPES = [
  {
    id: "self-employment",
    name: "Sole Trader",
    nature: "business",
    supportedReports: [
      Report.SA103S,
      Report.SA103F,
      Report.SNF,
      Report.CATEGORY_SUMMARY,
      Report.PROFIT_LOSS,
    ],
  },
  {
    id: "uk-property-non-fhl",
    name: "UK Property",
    nature: "landlord",
    supportedReports: [
      Report.SA105,
      Report.CATEGORY_SUMMARY,
      Report.PROFIT_LOSS,
    ],
  },
  {
    id: "uk-property-fhl",
    name: "UK Furnished Holiday Letting",
    nature: "landlord",
    supportedReports: [
      Report.SA105FHL,
      Report.CATEGORY_SUMMARY,
      Report.PROFIT_LOSS,
    ],
  },
  {
    id: "foreign-property-fhl-eea",
    name: "EEA Furnished Holiday Letting",
    nature: "landlord",
    supportedReports: [
      Report.SA105FHL,
      Report.CATEGORY_SUMMARY,
      Report.PROFIT_LOSS,
    ],
  },
  {
    id: "foreign-property",
    name: "Foreign Property",
    nature: "landlord",
    supportedReports: [
      Report.SA106,
      Report.CATEGORY_SUMMARY,
      Report.PROFIT_LOSS,
    ],
  },
  {
    id: "ltd-company",
    name: "Limited Company",
    nature: "business",
    supportedReports: [Report.CATEGORY_SUMMARY, Report.PROFIT_LOSS],
  },
] as IncomeStreamType[];

export const IMAGE_ATTACHMENT_EXTENSIONS = ["jpeg", "jpg", "png"];

export const SUPPORTED_ATTACHMENT_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
];
export enum MTDObligationQuarter {
  Q1 = "Q1",
  Q2 = "Q2",
  Q3 = "Q3",
  Q4 = "Q4",
}
export enum MTDObligationStatus {
  OPEN = "open",
  FULFILLED = "fulfilled",
  UPDATING = "updating",
}

export enum NONFINANCIALOPTIONS_VALUES {
  NON_EXEMPT = "non-exempt",
  NON_RESIDENT = "non-resident",
  TRUSTEE = "trustee",
  DIVER = "diver",
  ITTOIA_2005 = "ITTOIA-2005",
  OVER_STATE_PENSION_AGE = "over-state-pension-age",
  UNDER_16 = "under-16",
}

export const EXPORT_ENABLED_TAX_YEARS = ["2022", "2023", "2024", "2025"];

export const NONFINANCIALOPTIONS: nonFinancialoptionsType[] = [
  { value: NONFINANCIALOPTIONS_VALUES.NON_EXEMPT, name: "I am NOT exempt" },
  { value: NONFINANCIALOPTIONS_VALUES.NON_RESIDENT, name: "Non Resident" },
  { value: NONFINANCIALOPTIONS_VALUES.TRUSTEE, name: "Trustee" },
  { value: NONFINANCIALOPTIONS_VALUES.DIVER, name: "Diver" },
  { value: NONFINANCIALOPTIONS_VALUES.ITTOIA_2005, name: "ITTOIA 2005" },
  {
    value: NONFINANCIALOPTIONS_VALUES.OVER_STATE_PENSION_AGE,
    name: "Over State Pension Age",
  },
  { value: NONFINANCIALOPTIONS_VALUES.UNDER_16, name: "Under 16" },
];

export enum WANT_TO_CLAIM_TRADING_ALLOWANCE_OPTIONS_VALUES {
  YES = "yes",
  NO = "no",
}

export const DEFAULT_ANNUAL_SUBMISSION: AnnualSubmissionDataTypes = {
  adjustments: {
    includedNonTaxableProfits: 0,
    basisAdjustment: 0,
    overlapReliefUsed: 0,
    accountingAdjustment: 0,
    averagingAdjustment: 0,
    outstandingBusinessIncome: 0,
    balancingChargeBpra: 0,
    balancingChargeOther: 0,
    goodsAndServicesOwnUse: 0,
    transitionProfitAmount: 0,
    transitionProfitAccelerationAmount: 0,
  },
  allowances: {
    wantToClaimTradingAllowance:
      WANT_TO_CLAIM_TRADING_ALLOWANCE_OPTIONS_VALUES.NO,
    tradingIncomeAllowance: 0,
    annualInvestmentAllowance: 0,
    capitalAllowanceMainPool: 0,
    capitalAllowanceSpecialRatePool: 0,
    businessPremisesRenovationAllowance: 0,
    enhancedCapitalAllowance: 0,
    allowanceOnSales: 0,
    capitalAllowanceSingleAssetPool: 0,
    zeroEmissionsCarAllowance: 0,
    structuredBuildingAllowance: [],
    enhancedStructuredBuildingAllowance: [],
  },

  nonFinancials: {
    businessDetailsChangedRecently: false,
    class4NicsExemptionReason: NONFINANCIALOPTIONS[0].value,
  },
};

export const STRUCTURED_BILLING_ALLOWANCE_FIELDS = [
  ["Amount", "amount", "number"],
  ["Qualifying Date", "firstYear.qualifyingDate", "date"],
  [
    "Qualifying Amount Expenditure",
    "firstYear.qualifyingAmountExpenditure",
    "number",
  ],
  ["Building Name", "building.name", "text"],
  ["Building Number", "building.number", "text"],
  ["Postcode", "building.postcode", "text"],
];

export const ANNUAL_SUBMISSION_FORM_LABELS = {
  adjustments: {
    includedNonTaxableProfits: "Included Non-Taxable Profits",
    basisAdjustment: "Basis Adjustment",
    overlapReliefUsed: "Overlap Relief Used",
    accountingAdjustment: "Accounting Adjustment",
    averagingAdjustment: "Averaging Adjustment",
    outstandingBusinessIncome: "Outstanding Business Income",
    balancingChargeBpra: "Balancing Charge (BPRA)",
    balancingChargeOther: "Balancing Charge (Other)",
    goodsAndServicesOwnUse: "Goods & Services for Own Use",
    transitionProfitAmount: "Transition Profit Amount",
    transitionProfitAccelerationAmount: "Transition Profit Acceleration",
  },
  allowances: {
    wantToClaimTradingAllowance: "Want To Claim Trading Allowance?",
    tradingIncomeAllowance: "Trading Income Allowance",
    annualInvestmentAllowance: "Annual Investment Allowance",
    capitalAllowanceMainPool: "Capital Allowance Main Pool",
    capitalAllowanceSpecialRatePool: "Capital Allowance Special Rate Pool",
    businessPremisesRenovationAllowance:
      "Zero Emissions Goods Vehicle Allowance",
    enhancedCapitalAllowance: "Enhanced Capital Allowance",
    allowanceOnSales: "Allowance On Sales",
    capitalAllowanceSingleAssetPool: "Capital Allowance Single Asset Pool",
    zeroEmissionsCarAllowance: "Zero Emissions Car Allowance",
    structuredBuildingAllowance: "Structured Building Allowance",
    enhancedStructuredBuildingAllowance:
      "Enhanced Structured Building Allowance",
  },
  nonFinancials: {
    businessDetailsChangedRecently: "Business Details Changed Recently",
    class4NicsExemptionReason: "Class 4 NICs Exemption Reason",
  },
};

export const MTD_MIN_START_DATE = "2025-04-06";

export const REQUIRED_FIELDS_IF_NOCLAIM = [
  "annualInvestmentAllowance",
  "capitalAllowanceMainPool",
  "capitalAllowanceSpecialRatePool",
  "businessPremisesRenovationAllowance",
  "enhancedCapitalAllowance",
  "allowanceOnSales",
  "capitalAllowanceSingleAssetPool",
  "zeroEmissionsCarAllowance",
];

export const WANT_TO_CLAIM_TRADING_ALLOWANCE_OPTIONS = [
  { value: WANT_TO_CLAIM_TRADING_ALLOWANCE_OPTIONS_VALUES.YES, label: "Yes" },
  { value: WANT_TO_CLAIM_TRADING_ALLOWANCE_OPTIONS_VALUES.NO, label: "No" },
];

export const EXCLUDED_ALLOWANCE_KEYS = [
  "wantToClaimTradingAllowance",
  "tradingIncomeAllowance",
  "structuredBuildingAllowance",
  "enhancedStructuredBuildingAllowance",
];

export const QUARTERLY_PERIOD_TYPE_OPTIONS = [
  { name: "Calendar", value: "calendar" },
  { name: "Standard", value: "standard" },
];

export enum EstimateType {
  IN_YEAR = "Year to date Estimate",
  FULL_YEAR = "Full Year Estimate",
}
export const CHARGEBEE_BUSINESS_API_DELAY = 15000;

export const UpgradeButtonVariant = {
  DEFAULT: "default",
  TRIAL: "trial",
  ACTION: "action",
} as const;

export const CHARGEBEE_INACTIVE_PLAN_SKU = "core_inactive_v1";
export const HMRC_AUTHENTICATION_EXPIRED_EVENT = "hmrc-auth-expired";
export const HMRC_USER_INVALID = "hmrc-user-invalid";
